import { useState, useEffect, useCallback } from 'react';
import type { PricingProfileConfig } from '@techgitter/contracts';
import {
  getProfilesConfig,
  invalidateProfilesConfigCache,
} from '../services/pricingProfilesCacheService';

let cachedConfig: PricingProfileConfig | null = null;

export function usePricingProfilesConfig() {
  const [config, setConfig] = useState<PricingProfileConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isStale, setIsStale] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (signal?: AbortSignal) => {
    setIsLoading(true);
    setError(null);
    setIsStale(false);

    try {
      const result = await getProfilesConfig();

      if (signal?.aborted) return;

      cachedConfig = result.config;
      setConfig(result.config);
      setIsStale(result.isStale ?? false);

      if (result.warning) {
        console.warn('[PricingProfilesConfig]', result.warning);
      }
    } catch (err: any) {
      if (signal?.aborted) return;
      setError(err.message || 'Failed to load pricing profiles config');
    } finally {
      if (!signal?.aborted) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const retry = useCallback(() => {
    invalidateProfilesConfigCache();
    cachedConfig = null;
    load();
  }, [load]);

  return { config, isLoading, isStale, error, retry };
}

/**
 * Synchronous access to the pricing profiles config.
 * Returns null if the config hasn't been loaded yet.
 * Intended for use in pricing rules where the config is already loaded.
 */
export function getPricingProfilesConfig(): PricingProfileConfig | null {
  return cachedConfig;
}
